import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, Link2, Mail, MessageCircle, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useSEO } from "@/lib/seo";
import { api, CONTACT_EMAIL, mediaUrl, WHATSAPP_LINK } from "@/lib/api";
import { coverOf, formatKm, formatPrice, vehicleTitle } from "@/lib/format";
import { ParallaxImage, Reveal, Stagger, StaggerItem } from "@/components/site/Motion";
import { StatusBadge, VehicleCard } from "@/components/site/VehicleCard";
import { ContactForm } from "@/components/site/ContactBlock";

const ShareButton = ({ title }) => {
  const { t, lang } = useLang();
  const url = window.location.href;
  const text = lang === "fr" ? `${title} — Palissiere Motor Group` : `${title} — Palissiere Motor Group`;
  const waShare = `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("d_link_copied"));
    } catch {
      toast.error(url);
    }
  };
  const native = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: text, url }); } catch { /* cancelled */ }
    } else copy();
  };
  return (
    <div className="mt-2 flex items-center gap-4" data-testid="vehicle-share">
      <a href={waShare} target="_blank" rel="noreferrer" data-testid="vehicle-share-whatsapp" className="mono inline-flex items-center gap-2 text-[0.62rem] text-[#c7ccd4] hover:text-white">
        <Share2 size={13} /> {t("d_share_whatsapp")}
      </a>
      <button type="button" onClick={native} data-testid="vehicle-share-copy" className="mono inline-flex items-center gap-2 text-[0.62rem] text-[#c7ccd4] hover:text-white">
        <Link2 size={13} /> {t("d_share_link")}
      </button>
    </div>
  );
};

const Block = ({ title, children, testId }) => (
  <Reveal className="grid gap-6 border-t border-white/10 py-12 lg:grid-cols-12" data-testid={testId}>
    <h2 className="eyebrow lg:col-span-3">{title}</h2>
    <div className="text-base leading-relaxed text-[#dfe3e8] lg:col-span-8 lg:col-start-5">{children}</div>
  </Reveal>
);

export default function VehicleDetail() {
  const { slug } = useParams();
  const { t, lang, bi } = useLang();
  const [lightbox, setLightbox] = useState(null);
  const { data: v, isLoading, error } = useQuery({ queryKey: ["vehicle", slug], queryFn: async () => (await api.get(`/vehicles/${slug}`)).data, retry: false });
  const title = v ? `${vehicleTitle(v)}${v.version ? ` ${v.version}` : ""}` : "";
  const cover = v ? coverOf(v) : {};
  useSEO({
    title: v ? bi(v.seo_title) || `${title} — Palissiere Motor Group` : "Palissiere Motor Group",
    description: v ? bi(v.seo_description) || bi(v.short_description) : "",
    image: cover.full ? mediaUrl(cover.full) : undefined,
    lang, type: "product",
    jsonLd: v ? {
      "@context": "https://schema.org", "@type": "Car", name: title, brand: { "@type": "Brand", name: v.make }, model: v.model,
      vehicleModelDate: v.year, mileageFromOdometer: v.mileage ? { "@type": "QuantitativeValue", value: v.mileage, unitCode: "KMT" } : undefined,
      image: v.images?.map((i) => mediaUrl(i.url)), color: v.exterior_color,
      offers: v.price_visible && v.price ? { "@type": "Offer", price: v.price, priceCurrency: v.currency, availability: v.status === "available" ? "https://schema.org/InStock" : "https://schema.org/SoldOut" } : undefined,
    } : null,
  });

  if (isLoading) return <div className="h-[100svh] bg-[#070809]" />;
  if (error || !v) return (
    <div className="container-x flex min-h-[70vh] flex-col items-start justify-center pt-32">
      <h1 className="display text-4xl">404</h1>
      <Link to="/vehicles" className="btn btn-ghost mt-8">{t("d_back")}</Link>
    </div>
  );

  const price = v.price_visible && v.price != null ? formatPrice(v.price, v.currency, lang) : t("price_on_request");
  const specs = [
    ["s_make", v.make], ["s_model", v.model], ["s_version", v.version], ["s_year", v.year], ["s_mileage", v.mileage != null ? formatKm(v.mileage, lang) : ""],
    ["s_engine", v.engine], ["s_power", v.power], ["s_torque", v.torque], ["s_transmission", v.transmission], ["s_fuel", v.fuel],
    ["s_ext", v.exterior_color], ["s_int", v.interior_color], ["s_owners", v.owners], ["s_service", v.service_history],
    ["s_location", [v.location, v.country].filter(Boolean).join(", ")],
  ].filter(([, val]) => val !== "" && val != null);
  const waText = encodeURIComponent(lang === "fr" ? `Bonjour, je suis intéressé par la ${title} (${window.location.href}).` : `Hello, I am interested in the ${title} (${window.location.href}).`);
  const mailHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(title)}&body=${waText}`;

  return (
    <article className="bg-[#070809]" data-testid="vehicle-detail">
      <section className="relative h-[85svh] min-h-[520px] overflow-hidden">
        {v.video_url ? (
          <video className="absolute inset-0 h-full w-full object-cover" src={mediaUrl(v.video_url)} poster={mediaUrl(cover.full)} autoPlay muted loop playsInline />
        ) : (
          cover.full && <ParallaxImage src={mediaUrl(cover.full)} alt={cover.alt} className="absolute inset-0" strength={8} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#070809] via-[#070809]/20 to-[#070809]/40" />
        <div className="container-x relative flex h-full flex-col justify-end pb-14 md:pb-20">
          <Link to="/vehicles" data-testid="vehicle-back" className="mono mb-8 inline-flex w-fit items-center gap-2 text-[0.62rem] text-[#c7ccd4] hover:text-white"><ArrowLeft size={14} /> {t("d_back")}</Link>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="mb-4 flex items-center gap-4"><StatusBadge status={v.status} /><span className="mono text-[0.6rem] text-[#8b95a5]">{v.year}{v.location ? ` · ${v.location}` : ""}</span></div>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="display text-4xl sm:text-5xl lg:text-7xl" data-testid="vehicle-title">
                {v.make} <span className="block text-[#dfe3e8]">{v.model}</span>
              </motion.h1>
              {v.version && <p className="mt-3 text-base text-[#c7ccd4] md:text-lg">{v.version}</p>}
            </div>
            <div className="text-right">
              <p className="eyebrow mb-2 justify-end before:hidden">{t("s_price")}</p>
              <p className="display-tight text-2xl md:text-3xl" data-testid="vehicle-price">{price}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="container-x">
        <Reveal className="grid gap-10 py-14 lg:grid-cols-12 md:py-20">
          <p className="display-tight text-xl leading-snug text-[#f4f5f7] sm:text-2xl lg:col-span-7 lg:text-3xl" data-testid="vehicle-short-description">{bi(v.short_description)}</p>
          <div className="flex flex-col gap-3 lg:col-span-4 lg:col-start-9">
            <a href="#inquiry" data-testid="vehicle-cta-request" className="btn btn-primary">{t("d_request")}</a>
            <a href={`${WHATSAPP_LINK}?text=${waText}`} target="_blank" rel="noreferrer" data-testid="vehicle-cta-whatsapp" className="btn btn-whatsapp"><MessageCircle size={15} /> WhatsApp</a>
            <a href={mailHref} data-testid="vehicle-cta-email" className="btn btn-ghost"><Mail size={15} /> Email</a>
            <ShareButton title={title} />
            <Link to={`/sourcing?make=${encodeURIComponent(v.make)}&model=${encodeURIComponent(v.model)}`} data-testid="vehicle-cta-similar" className="mono link-underline mt-2 w-fit text-[0.62rem] text-[#c7ccd4]">{t("d_similar")}</Link>
          </div>
        </Reveal>

        <section className="border-t border-white/10 py-12" data-testid="vehicle-specs">
          <h2 className="eyebrow mb-8">{t("d_specs")}</h2>
          <Stagger className="grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {specs.map(([k, val]) => (
              <StaggerItem key={k} className="bg-[#0b0d10] p-5">
                <p className="mono text-[0.58rem] text-[#8b95a5]">{t(k)}</p>
                <p className="mt-2 text-sm font-medium text-[#f4f5f7]">{val}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </section>

        {v.images?.length > 1 && (
          <section className="border-t border-white/10 py-12" data-testid="vehicle-gallery">
            <h2 className="eyebrow mb-8">{t("d_gallery")}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {v.images.map((img, i) => (
                <Reveal key={i} delay={(i % 3) * 0.06} className={i % 4 === 0 ? "sm:col-span-2 lg:col-span-2" : ""}>
                  <button onClick={() => setLightbox(i)} data-testid={`gallery-image-${i}`} className="hover-zoom block aspect-[4/3] w-full overflow-hidden bg-[#0e1013]">
                    <img src={mediaUrl(img.thumb || img.url)} alt={img.alt || title} loading="lazy" className="h-full w-full object-cover" />
                  </button>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {bi(v.description) && <Block title={t("d_description")} testId="vehicle-description"><p className="whitespace-pre-line">{bi(v.description)}</p></Block>}
        {v.equipment?.length > 0 && (
          <Block title={t("d_equipment")} testId="vehicle-equipment">
            <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2">{v.equipment.map((e) => <li key={e} className="border-b border-white/10 pb-3 text-sm">{e}</li>)}</ul>
          </Block>
        )}
        {bi(v.history) && <Block title={t("d_history")} testId="vehicle-history"><p className="whitespace-pre-line">{bi(v.history)}</p></Block>}
        {bi(v.condition) && <Block title={t("d_condition")} testId="vehicle-condition"><p className="whitespace-pre-line">{bi(v.condition)}</p></Block>}
        {(v.location || v.country) && <Block title={t("d_location")} testId="vehicle-location"><p>{[v.location, v.country].filter(Boolean).join(", ")}</p></Block>}

        <section id="inquiry" className="grid gap-12 border-t border-white/10 py-16 lg:grid-cols-12 md:py-24" data-testid="vehicle-inquiry">
          <Reveal className="lg:col-span-5">
            <h2 className="display text-3xl sm:text-4xl">{t("d_inquiry_title")}</h2>
            <p className="mt-5 max-w-sm text-[#c7ccd4]">{t("d_inquiry_copy")}</p>
            <p className="mono mt-8 text-[0.62rem] text-[#8b95a5]">{title}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7"><ContactForm type="inquiry" vehicle={title} vehicleId={v.id} compact /></Reveal>
        </section>

        {v.similar?.length > 0 && (
          <section className="border-t border-white/10 py-16 md:py-24" data-testid="vehicle-similar">
            <h2 className="eyebrow mb-10">{t("d_more")}</h2>
            <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">{v.similar.map((s) => <VehicleCard key={s.id} v={s} />)}</div>
          </section>
        )}
      </div>

      {lightbox != null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button className="absolute right-5 top-5 p-2 text-white" aria-label="Close" data-testid="lightbox-close"><X /></button>
          <img src={mediaUrl(v.images[lightbox].url)} alt="" className="max-h-full max-w-full object-contain" />
          <div className="absolute bottom-6 flex gap-2">
            {v.images.map((_, i) => <button key={i} onClick={(e) => { e.stopPropagation(); setLightbox(i); }} className={`h-1 w-8 ${i === lightbox ? "bg-white" : "bg-white/30"}`} />)}
          </div>
        </div>
      )}
    </article>
  );
}
